import { MutableRefObject } from 'react';
import { School } from '@common-types';
import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';

import { moneyFormatter } from '@utils/formatter';
import { CLARIFY_COST_TOOLTIP } from '@constants';

import CheckMarkIcon from '@images/checkmark-in-cercle.svg?react';
import InfoIcon from '@images/info.svg?react';

import {
  sxStyles,
  StyledTuitionCostWrap,
  StyledAverageCostOfAttendanceTitle,
  StyledTuitionCostInner,
  StyledCalculateCostWrap,
  StyledCalculateCostLink,
} from './TuitionCost.styles';
import { PageSectionIds } from '@constants/schoolDetailsPage';
import { SectionLinkOffset } from '@pages/SchoolDetails/SchoolDetails.styles';

const renderCostItem = (title: string, cost?: number | string) => {
  if (!cost) return null;

  return (
    <CoreBox sx={sxStyles.costItemWrap}>
      <CoreTypography sx={sxStyles.costItemTitle} component="div" variant="body2">
        {title}
      </CoreTypography>

      <CoreTypography component="div" variant="h6">
        {moneyFormatter.format(+cost)}
      </CoreTypography>
    </CoreBox>
  );
};

interface TuitionCostProps {
  scrollToRef: MutableRefObject<HTMLAnchorElement | null>;
  school: School;
}

export const TuitionCost = (props: TuitionCostProps) => {
  const {
    scrollToRef,
    school: { characteristics, costs, collegeScorecard, directory },
  } = props;

  const preparedCostPerYear = collegeScorecard?.latest?.cost?.attendance?.academic_year;
  const preparedProgramYearInstitutions = collegeScorecard?.latest?.cost?.attendance?.program_year;

  const isAverageCostOfAttendance = preparedCostPerYear || preparedProgramYearInstitutions;
  const isAverageCostByLocation = costs?.outOfStateTuition || costs?.inStateTuition;
  const isOtherExpenses =
    costs?.onCampusOtherExpenses ||
    costs?.onCampusHousing ||
    costs?.booksAndSupplies ||
    costs?.offCampusOtherExpenses ||
    costs?.offCampusHousing;
  const isHaveData = isAverageCostOfAttendance || isAverageCostByLocation || isOtherExpenses;

  const priceCalculatorUrl = directory?.links.priceCalculatorUrl?.match(/^[a-zA-Z]+:\/\//)
    ? directory?.links.priceCalculatorUrl
    : `https://${directory?.links.priceCalculatorUrl}`;

  return (
    <CoreBox sx={sxStyles.tuitionCostContainer}>
      <SectionLinkOffset id={PageSectionIds['tuition-cost-aid']} ref={scrollToRef} />

      <CoreTypography sx={sxStyles.tuitionCostSectionTitle} variant="h4" component="h3">
        Tuition, cost, and aid
      </CoreTypography>

      <StyledTuitionCostWrap>
        <CoreTypography sx={sxStyles.costAveragesTitle} variant="h5" component="h4">
          Cost averages
        </CoreTypography>

        {isHaveData && (
          <StyledTuitionCostInner>
            <CoreBox>
              {isAverageCostOfAttendance && (
                <CoreBox sx={sxStyles.averageCostOfAttendanceWrap}>
                  <StyledAverageCostOfAttendanceTitle>
                    <CoreTypography component="div" variant="overline">
                      Average cost of attendance
                    </CoreTypography>

                    <CoreTooltip title={CLARIFY_COST_TOOLTIP}>
                      <CoreBox>
                        <InfoIcon />
                      </CoreBox>
                    </CoreTooltip>
                  </StyledAverageCostOfAttendanceTitle>

                  {preparedCostPerYear && (
                    <>
                      <CoreTypography variant="body2">Overall costs per year</CoreTypography>

                      <CoreTypography sx={sxStyles.costsPerYear} variant="h3" component="h2">
                        {moneyFormatter.format(preparedCostPerYear)}
                      </CoreTypography>
                    </>
                  )}

                  {renderCostItem('Program-year institutions', preparedProgramYearInstitutions || 0)}
                </CoreBox>
              )}

              {isAverageCostByLocation && (
                <CoreTypography variant="overline" component="h5">
                  Average cost by location
                </CoreTypography>
              )}

              {renderCostItem('Out-of-state tuition and fees', costs?.outOfStateTuition)}

              {renderCostItem('In-state tuition and fees', costs?.inStateTuition)}

              {characteristics?.hasOnCampusHousing && (
                <CoreBox sx={sxStyles.onCampusHousing}>
                  <CoreBox>
                    <CheckMarkIcon />
                  </CoreBox>

                  <CoreTypography>On-campus housing available</CoreTypography>
                </CoreBox>
              )}
            </CoreBox>

            {isOtherExpenses && (
              <CoreBox>
                <CoreTypography variant="overline" component="h5">
                  Average cost for other expenses
                </CoreTypography>

                {renderCostItem('On-campus room and board', costs?.onCampusHousing)}

                {renderCostItem('On-campus other expenses', costs?.onCampusOtherExpenses)}

                {renderCostItem('Off-campus room and board', costs?.offCampusHousing)}

                {renderCostItem('Off-campus other expenses', costs?.offCampusOtherExpenses)}

                {renderCostItem('Estimated books and supplies', costs?.booksAndSupplies)}
              </CoreBox>
            )}
          </StyledTuitionCostInner>
        )}

        {directory?.links.priceCalculatorUrl && (
          <StyledCalculateCostWrap>
            <CoreTypography component="div" variant="h6">
              Interested in estimating the cost based on your information?
            </CoreTypography>

            <StyledCalculateCostLink rel="noreferrer" target="_blank" href={priceCalculatorUrl}>
              Calculate the Cost
            </StyledCalculateCostLink>
          </StyledCalculateCostWrap>
        )}
      </StyledTuitionCostWrap>
    </CoreBox>
  );
};
